import { IconDefinition } from "models/propTypes/iconPropTypes";
import { ComponentBaseProps } from "models/propTypes/basePropTypes";
import { StyleEnums } from "models/enums";
import { DialogActions, DialogProps } from "models/propTypes/dialogPropTypes";
import React, { ReactElement } from "react";
import { RoundButtonProps, StyledButtonProps } from "models/propTypes/commonPropTypes";

export type PopMenuEventProps = {
  render(
    anchorEl: HTMLElement | null,
    isOpen: boolean,
    openMenu: (event: React.MouseEvent<HTMLButtonElement>) => void,
    closeMenu: (event?: React.MouseEvent<HTMLButtonElement>) => void,
    isClosing: boolean
  ): void;
  menuStateChangeCallback?: (isOpen: boolean) => void;
  allowClickEventPropagation?: boolean;
};

export interface StyledMenuProps {
  anchorEl: HTMLElement | null;
  title?: string;
  itemList?: StyledMenuItemProps[];
  actionList?: StyledMenuActionProps[];
  onMenuClickHandler?: (id: number) => void;
  closeMenu: () => void;
}

export interface CommonMenuItemProps extends ComponentBaseProps {
  text: string;
  icon?: IconDefinition;
  disabled?: boolean;
  selected?: boolean;
  onClick: () => void;
}

export interface StyledMenuItemProps {
  id: number;
  text: string;
  selected?: boolean;
}

export interface StyledMenuActionProps {
  text: string;
  icon: IconDefinition;
  disabled?: boolean;
  onClickHandler: () => void;
}

export interface MenuButtonProps {
  /** Menu items */
  menuItems: MoreActionMenuItemType[];
  /** Handler to run on any menu item click success */
  onActionSuccess?: (result?: any) => void;
  /** Handler to run on any menu item click fails */
  onError?: (error: any) => void;
  /** Button component i.e. StyledButton or RoundButton */
  button: React.ReactElement<StyledButtonProps | RoundButtonProps>;
  /** Classes */
  classes?: {
    root?: string;
    menu?: string;
    menuOpen?: string;
    menuClosing?: string;
  };
  /** Override button content on menu open */
  contentOnOpen?: React.ReactElement;
  /** Menu placement */
  menuPlacement?: "top" | "bottom" | "right" | "bottom-start" | "bottom-end";
  /** Disable the portal behavior. The children stay within it's parent DOM hierarchy. */
  disablePopperPortal?: boolean;
  /** Set a custom offset for the menu popper */
  popperOffSet?: number[];
  /** To allow click event propagation, since the default behavior is to stop event propagation */
  allowClickEventPropagation?: boolean;
  /** Disable success messages */
  disableSuccessMessages?: boolean;
  /** If set to true this will blur background when menu is open. Default is false.*/
  blurBackgroundOnOpen?: boolean;
  menuStateChangeCallback?: (isOpen: boolean) => void;
}

export interface PopperMenuButtonProps {
  /** Button component i.e. StyledButton or RoundButton */
  button: React.ReactElement<StyledButtonProps | RoundButtonProps>;
  /** Classes */
  classes?: {
    root?: string;
    menu?: string;
    menuOpen?: string;
    menuClosing?: string;
  };
  /** Override button content on menu open */
  contentOnOpen?: React.ReactElement;
  /** Menu placement */
  menuPlacement?: "top" | "bottom" | "right" | "bottom-start" | "bottom-end";
  /** Disable the portal behavior. The children stay within it's parent DOM hierarchy. */
  disablePopperPortal?: boolean;
  /** Set a custom offset for the menu popper */
  popperOffSet?: number[];
  /** To allow click event propagation, since the default behavior is to stop event propagation */
  allowClickEventPropagation?: boolean;
  /** Popper content */
  children: ((closeMenu: () => void) => React.ReactElement) | React.ReactElement;
  /** If set to true this will blur background when menu is open. Default is false.*/
  blurBackgroundOnOpen?: boolean;
  /** Handler to run when the Popper is closed */
  onClose?: () => void;
  blockOutsideClick?: boolean;
  overrideCloseMenu?: (closeMenu: (event?: React.MouseEvent<HTMLButtonElement>) => void) => void;
}

export const ACTION_SEPARATOR = "ACTION_SEPARATOR";

export type MoreActionMenuItemType = MoreActionMenuItem | string | typeof ACTION_SEPARATOR;

export interface MoreActionMenuItem {
  menuKey?: string;
  description: string;
  icon: IconDefinition;
  actionDialog?: DialogProps;
  onClickHandler?: () => Promise<any>;
  onActionSuccess?: (result?: any) => void;
  successMessage?: string;
  inProgressMessage?: string;
  highlightType?: StyleEnums.DisplayType;
  hidden?: boolean;
  iconClassName?: string;
}

export interface ButtonGroupProps extends ComponentBaseProps {
  buttons: (React.ReactElement | typeof ACTION_SEPARATOR)[];
  moreActions?: MoreActionMenuItemType[];
}

export interface DialogActionButtonProps extends StyledButtonProps {
  actionDialog: (onCreateSuccess?: (() => void) | undefined) => DialogProps;
  reload: () => void;
}

export interface PopperFormProps {
  title: string | ReactElement;
  subTitle?: string | ReactElement;
  splashText?: string | ReactElement;
  children: ReactElement;
  actions?: DialogActions[];
  moreActions?: MoreActionMenuItemType[] | ((closePopper: () => void, isValid?: boolean) => MoreActionMenuItemType[]);
  footer?: ReactElement | ((closePopper: () => void, isValid?: boolean) => ReactElement);
  closePopper?: () => void;
  onSubmit?: (values?: any) => void;
  onError?: (values?: any, closePopper?: () => void) => void;
  hideCancelButton?: boolean;
  disbleSuccessMessages?: boolean;
}
